// API client [02 §6.2-6.5]: house envelope, error envelope → ApiError, If-Match on every mutation of an
// existing row, Idempotency-Key on every action/create/put/delete/upload, 412 → StaleVersionError carrying the
// current row. `put`, `delete` and `upload` serve the admin and specs/BOM screens (CR-W2a-35). `raw` serves the
// Better Auth routes, which answer outside the envelope (CR-W2a-38).
import type { ErrorBody } from '@skm/core';

export class ApiError extends Error {
  readonly status: number;
  readonly body: ErrorBody;
  constructor(status: number, body: ErrorBody) {
    super(body.message || body.code);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** 412: someone changed the row; `current` is the server's representation (02 §6.2 `error.params.current`). */
export class StaleVersionError extends ApiError {
  readonly current: unknown;
  constructor(status: number, body: ErrorBody) {
    super(status, body);
    this.name = 'StaleVersionError';
    this.current =
      (body.details as { current?: unknown } | undefined)?.current ?? body.params?.current ?? null;
  }
}

/** Cookie-session writes must carry this header or the API answers 403 `csrf` (02 §7; apps/api platform/auth). */
const CLIENT_HEADER = { 'X-SKM-Client': 'web' } as const;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof fetch;
  readonly newKey?: () => string;
  readonly locale?: () => string;
}

export interface MutationOptions {
  /** Row version (ETag) of the record being changed; mandatory for PATCH/actions on existing rows. */
  readonly ifMatch?: string;
  /** Reuse a key when retrying the same user action (02 §6.5). */
  readonly idempotencyKey?: string;
  /**
   * The route answers outside the house envelope (Better Auth under `/v1/auth/*`, `idempotency: 'off'`): resolve to
   * the JSON body as is (`null` when empty) and send no Idempotency-Key. A failure that is not a house error
   * envelope throws an `ApiError` carrying the vendor answer in `body.details.vendor`.
   */
  readonly raw?: boolean;
}

/** `upload` options: raw-body verb (PUT replaces a stable resource, POST creates one) and the content type. */
export interface UploadOptions extends MutationOptions {
  /** Defaults to the blob's own type, then `application/octet-stream`. */
  readonly contentType?: string;
  /** Defaults to PUT (help videos); attachments are created with POST. */
  readonly method?: 'PUT' | 'POST';
}

/**
 * What a screen receives. `put`, `delete` and `upload` are optional here so a screen (or a test double) that does
 * not use them type-checks against a smaller client; the real client is a {@link FullApiClient} and always has them.
 */
export interface ApiClient {
  get<T>(path: string): Promise<T>;
  create<T>(path: string, body: unknown, opts?: MutationOptions): Promise<T>;
  patch<T>(path: string, body: unknown, opts: MutationOptions & { ifMatch: string }): Promise<T>;
  action<T>(path: string, body: unknown, opts: MutationOptions & { ifMatch: string }): Promise<T>;
  put?<T>(path: string, body: unknown, opts?: MutationOptions): Promise<T>;
  /** A 204 answer resolves to `undefined`. */
  delete?<T>(path: string, opts?: MutationOptions): Promise<T>;
  upload?<T>(path: string, body: Blob, opts?: UploadOptions): Promise<T>;
}

export interface FullApiClient extends ApiClient {
  put<T>(path: string, body: unknown, opts?: MutationOptions): Promise<T>;
  delete<T>(path: string, opts?: MutationOptions): Promise<T>;
  upload<T>(path: string, body: Blob, opts?: UploadOptions): Promise<T>;
}

const fallbackError = (status: number, vendor?: unknown): ErrorBody => ({
  code: status >= 500 ? 'INTERNAL' : 'BUSINESS_RULE',
  messageKey: status >= 500 ? 'errors.code.INTERNAL' : 'errors.code.BUSINESS_RULE',
  message: '',
  ...(vendor !== undefined && vendor !== null ? { details: { vendor } } : {}),
});

/** A request body: JSON-encoded unless it is a raw blob with its own content type. */
interface Payload {
  readonly body: BodyInit;
  readonly contentType: string;
}
const jsonPayload = (body: unknown): Payload | undefined =>
  body === undefined ? undefined : { body: JSON.stringify(body), contentType: 'application/json' };

export function createApiClient(opts: ApiClientOptions): FullApiClient {
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const newKey = opts.newKey ?? (() => crypto.randomUUID());
  async function call<T>(
    method: string,
    path: string,
    payload?: Payload,
    headers: Record<string, string> = {},
    raw = false,
  ): Promise<T> {
    const res = await doFetch(`${opts.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...CLIENT_HEADER,
        ...(payload ? { 'Content-Type': payload.contentType } : {}),
        ...(opts.locale ? { 'Accept-Language': opts.locale() } : {}),
        ...headers,
      },
      body: payload?.body,
    });
    let json: { data?: T; error?: ErrorBody | null } | null = null;
    try {
      json = (await res.json()) as { data?: T; error?: ErrorBody | null };
    } catch {
      json = null;
    }
    if (raw && res.ok) return json as T;
    if (!res.ok || json?.error) {
      const err = json?.error ?? fallbackError(res.status, raw ? json : undefined);
      if (res.status === 412) throw new StaleVersionError(res.status, err);
      throw new ApiError(res.status, err);
    }
    return json?.data as T;
  }
  const mutate = <T>(method: string, path: string, payload?: Payload, o: MutationOptions = {}) =>
    call<T>(
      method,
      path,
      payload,
      {
        ...(o.raw ? {} : { 'Idempotency-Key': o.idempotencyKey ?? newKey() }),
        ...(o.ifMatch ? { 'If-Match': o.ifMatch } : {}),
      },
      o.raw === true,
    );
  return {
    get: (path) => call('GET', path),
    create: (path, body, o) => mutate('POST', path, jsonPayload(body), o),
    patch: (path, body, o) => mutate('PATCH', path, jsonPayload(body), o),
    action: (path, body, o) => mutate('POST', path, jsonPayload(body), o),
    put: (path, body, o) => mutate('PUT', path, jsonPayload(body), o),
    delete: (path, o) => mutate('DELETE', path, undefined, o),
    upload: (path, body, o) =>
      mutate(
        o?.method ?? 'PUT',
        path,
        { body, contentType: o?.contentType || body.type || 'application/octet-stream' },
        o,
      ),
  };
}
